// src/components/DocumentEditorPage.js
//
// Full-featured document editor using TipTap, integrated with the
// existing ERP document system.
//
// Features:
//  - Rich text editing (bold, italic, underline, strike, highlight)
//  - Headings, lists, tables, images, links
//  - Export to DOCX, PDF, HTML via the doc-engine backend API
//  - Save content back to an existing ERP document (HTML body stored)
//  - Auto-save with 30-second debounce
//  - Approval workflow (Draft → Pending Review → Approved / Rejected)
//  - Inline comments (highlight + sidebar, Google Docs style)
//  - Version history panel
//  - Clause library quick-insert
//  - Document locking while editing
//  - Save as template
//  - Word / character count
//  - Legal template quick-insert
//  - Print support, Undo / Redo, keyboard shortcuts (Ctrl+S, Ctrl+P)

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { TextAlign } from '@tiptap/extension-text-align';
import { Highlight } from '@tiptap/extension-highlight';
import { Image as ImageExt } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Placeholder } from '@tiptap/extension-placeholder';
import CommentMark from './CommentExtension';
import DocumentCommentsSidebar from './DocumentCommentsSidebar';

import { API_BASE_URL } from '../config';
import { usePermissions } from '../App';
import './DocumentEditorPage.css';
import { useConfirm } from './ConfirmProvider';

// ---------- Heading levels for the dropdown ----------
const HEADING_LEVELS = [
  { label: 'Normal', level: 0 },
  { label: 'Heading 1', level: 1 },
  { label: 'Heading 2', level: 2 },
  { label: 'Heading 3', level: 3 },
  { label: 'Heading 4', level: 4 },
];

// ---------- SVG Icons (MS Word style) ----------
const IconSave = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z" /></svg>
);
const IconUndo = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z" /></svg>
);
const IconRedo = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z" /></svg>
);
const IconBold = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z" /></svg>
);
const IconItalic = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z" /></svg>
);
const IconUnderline = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z" /></svg>
);
const IconStrike = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M10 19h4v-3h-4v3zM5 4v3h5v3h4V7h5V4H5zM3 14h18v-2H3v2z" /></svg>
);
const IconHighlight = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M2 20h20v4H2v-4zm3.49-3H11v1.78L14.26 22l1.41-1.41L11 15.83V14H5.51l-.99 3zm8.3-14.01L6.13 10.64l4.24 4.24 7.65-7.65-4.23-4.24z" /></svg>
);
const IconAlignLeft = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M15 15H3v2h12v-2zm0-8H3v2h12V7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zM3 3v2h18V3H3z" /></svg>
);
const IconAlignCenter = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M7 15v2h10v-2H7zm-4 6h18v-2H3v2zm0-8h18v-2H3v2zm4-6v2h10V7H7zM3 3v2h18V3H3z" /></svg>
);
const IconAlignRight = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M3 21h18v-2H3v2zm6-4h12v-2H9v2zm-6-4h18v-2H3v2zm6-4h12V7H9v2zM3 3v2h18V3H3z" /></svg>
);
const IconAlignJustify = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M3 21h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18V7H3v2zM3 3v2h18V3H3z" /></svg>
);
const IconBulletList = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z" /></svg>
);
const IconOrderedList = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z" /></svg>
);
const IconTable = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M3 3v18h18V3H3zm8 16H5v-6h6v6zm0-8H5V5h6v6zm8 8h-6v-6h6v6zm0-8h-6V5h6v6z" /></svg>
);
const IconImage = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" /></svg>
);
const IconLink = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" /></svg>
);
const IconExport = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2v9.67z" /></svg>
);
const IconPrint = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z" /></svg>
);
const IconBack = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" /></svg>
);

// ===================== TOOLBAR COMPONENT =====================
function EditorToolbar({ editor }) {
  const confirm = useConfirm();

  if (!editor) return null;

  const btn = (active, onClick, title, Icon) => (
    <button
      className={`doc-toolbar-btn${active ? ' active' : ''}`}
      onClick={onClick}
      title={title}
      type="button"
    >
      <Icon />
    </button>
  );

  return (
    <div className="doc-toolbar">
      {/* Undo / Redo */}
      <div className="doc-toolbar-group">
        <button className="doc-toolbar-btn" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo (Ctrl+Z)" type="button"><IconUndo /></button>
        <button className="doc-toolbar-btn" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo (Ctrl+Y)" type="button"><IconRedo /></button>
      </div>

      <div className="doc-toolbar-divider" />

      {/* Heading */}
      <div className="doc-toolbar-group">
        <select
          className="doc-toolbar-select heading"
          onChange={(e) => {
            const level = parseInt(e.target.value);
            if (level === 0) {
              editor.chain().focus().setParagraph().run();
            } else {
              editor.chain().focus().toggleHeading({ level }).run();
            }
          }}
          title="Paragraph Style"
          value={
            editor.isActive('heading', { level: 1 }) ? 1
              : editor.isActive('heading', { level: 2 }) ? 2
              : editor.isActive('heading', { level: 3 }) ? 3
              : editor.isActive('heading', { level: 4 }) ? 4
              : 0
          }
        >
          {HEADING_LEVELS.map(({ label, level }) => (
            <option key={level} value={level}>{label}</option>
          ))}
        </select>
      </div>

      <div className="doc-toolbar-divider" />

      {/* Inline Formatting */}
      <div className="doc-toolbar-group">
        {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), 'Bold (Ctrl+B)', IconBold)}
        {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), 'Italic (Ctrl+I)', IconItalic)}
        {btn(editor.isActive('underline'), () => editor.chain().focus().toggleUnderline().run(), 'Underline (Ctrl+U)', IconUnderline)}
        {btn(editor.isActive('strike'), () => editor.chain().focus().toggleStrike().run(), 'Strikethrough', IconStrike)}
        {btn(editor.isActive('highlight'), () => editor.chain().focus().toggleHighlight().run(), 'Highlight', IconHighlight)}
      </div>

      <div className="doc-toolbar-divider" />

      {/* Alignment */}
      <div className="doc-toolbar-group">
        {btn(editor.isActive({ textAlign: 'left' }), () => editor.chain().focus().setTextAlign('left').run(), 'Align Left', IconAlignLeft)}
        {btn(editor.isActive({ textAlign: 'center' }), () => editor.chain().focus().setTextAlign('center').run(), 'Align Center', IconAlignCenter)}
        {btn(editor.isActive({ textAlign: 'right' }), () => editor.chain().focus().setTextAlign('right').run(), 'Align Right', IconAlignRight)}
        {btn(editor.isActive({ textAlign: 'justify' }), () => editor.chain().focus().setTextAlign('justify').run(), 'Justify', IconAlignJustify)}
      </div>

      <div className="doc-toolbar-divider" />

      {/* Lists */}
      <div className="doc-toolbar-group">
        {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), 'Bullet List', IconBulletList)}
        {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), 'Numbered List', IconOrderedList)}
      </div>

      <div className="doc-toolbar-divider" />

      {/* Insert */}
      <div className="doc-toolbar-group">
        <button className="doc-toolbar-btn" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert Table" type="button"><IconTable /></button>
        <button
          className="doc-toolbar-btn"
          onClick={async () => {
            const url = await confirm({ title: 'Insert Image', message: 'Enter the URL for the image.', confirmText: 'Insert', variant: 'primary', promptMode: true, promptLabel: 'Image URL', required: true });
            if (url) editor.chain().focus().setImage({ src: url }).run();
          }}
          title="Insert Image"
          type="button"
        >
          <IconImage />
        </button>
        <button
          className="doc-toolbar-btn"
          onClick={async () => {
            const url = await confirm({ title: 'Insert Link', message: 'Enter the URL for the link.', confirmText: 'Insert', variant: 'primary', promptMode: true, promptLabel: 'Link URL', required: true });
            if (url) editor.chain().focus().setLink({ href: url }).run();
          }}
          title="Insert Link"
          type="button"
        >
          <IconLink />
        </button>
      </div>
    </div>
  );
}

// ===================== LEGAL TEMPLATE SIDEBAR =====================
const LEGAL_TEMPLATES = [
  { id: 'nda', name: 'Non-Disclosure Agreement', category: 'Contracts', description: 'Standard NDA template with customizable terms' },
  { id: 'employment', name: 'Employment Agreement', category: 'Employment', description: 'Full employment contract template' },
  { id: 'service', name: 'Service Agreement', category: 'Contracts', description: 'Professional services engagement agreement' },
  { id: 'lease', name: 'Lease Agreement', category: 'Real Estate', description: 'Residential or commercial lease template' },
  { id: 'poa', name: 'Power of Attorney', category: 'Legal', description: 'General or special power of attorney' },
  { id: 'promissory', name: 'Promissory Note', category: 'Financial', description: 'Promissory note with payment terms' },
  { id: 'loi', name: 'Letter of Intent', category: 'Business', description: 'Letter of intent for business transactions' },
  { id: 'cease', name: 'Cease & Desist', category: 'Legal', description: 'Cease and desist demand letter' },
  { id: 'will', name: 'Last Will & Testament', category: 'Estate', description: 'Simple will template' },
  { id: 'bill_sale', name: 'Bill of Sale', category: 'Sales', description: 'Asset or property sale document' },
];

function TemplateSidebar({ authAxios, editor, onInsert }) {
  const [loading, setLoading] = useState(false);
  const [apiTemplates, setApiTemplates] = useState([]);

  // Fetch saved document templates from the API
  useEffect(() => {
    if (!authAxios) return;
    authAxios.get('document-templates/')
      .then((resp) => setApiTemplates(resp.data?.results || resp.data || []))
      .catch(() => {});
  }, [authAxios]);

  const handleTemplateInsert = async (templateId, isApi = false) => {
    if (!editor || !authAxios) return;
    setLoading(true);
    try {
      if (isApi) {
        // Insert from saved API template
        const tmpl = apiTemplates.find((t) => t.id === templateId);
        if (tmpl?.content) {
          editor.chain().focus().insertContent(tmpl.content).run();
        }
      } else {
        // Try the doc-engine API to generate the template
        const docEngineBase = API_BASE_URL.replace(/\/api\/$/, '/api/doc-engine/');
        const resp = await authAxios.get(`${docEngineBase}templates/${templateId}/`);
        if (resp.data?.preview_html) {
          editor.chain().focus().insertContent(resp.data.preview_html).run();
        } else if (resp.data?.content) {
          editor.chain().focus().insertContent(resp.data.content).run();
        }
      }
    } catch (err) {
      // Fallback: insert placeholder heading
      const tmpl = LEGAL_TEMPLATES.find((t) => t.id === templateId);
      editor
        .chain()
        .focus()
        .insertContent(`<h2>${tmpl?.name || 'Template'}</h2><p>[Template content will be populated here.]</p><hr/>`)
        .run();
    } finally {
      setLoading(false);
      onInsert?.();
    }
  };

  return (
    <div className="doc-template-sidebar">
      <h3>Legal Templates</h3>
      {loading && <div style={{ padding: '12px 16px', color: '#888', fontSize: '13px' }}>Loading template...</div>}

      {/* Saved templates from API */}
      {apiTemplates.length > 0 && (
        <>
          <div className="doc-template-section-title">Saved Templates</div>
          {apiTemplates.map((t) => (
            <div key={`api-${t.id}`} className="doc-template-item" onClick={() => handleTemplateInsert(t.id, true)}>
              <h4>{t.name}</h4>
              <p>{t.document_type || 'Custom Template'}</p>
            </div>
          ))}
          <div className="doc-template-section-title" style={{ marginTop: 12 }}>Built-in Templates</div>
        </>
      )}

      {LEGAL_TEMPLATES.map((t) => (
        <div key={t.id} className="doc-template-item" onClick={() => handleTemplateInsert(t.id)}>
          <h4>{t.name}</h4>
          <p>{t.description}</p>
        </div>
      ))}
    </div>
  );
}

// ===================== CLAUSE LIBRARY SIDEBAR =====================
function ClauseLibrarySidebar({ authAxios, editor, onInsert }) {
  const [clauses, setClauses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');

  useEffect(() => {
    if (!authAxios) return;
    setLoading(true);
    authAxios.get('legal-clauses/')
      .then((resp) => setClauses(resp.data?.results || resp.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authAxios]);

  const categories = [...new Set(clauses.map((c) => c.category))].sort();
  const filtered = filterCategory ? clauses.filter((c) => c.category === filterCategory) : clauses;

  return (
    <div className="doc-template-sidebar">
      <h3>Clause Library</h3>
      <select
        className="doc-clause-filter"
        value={filterCategory}
        onChange={(e) => setFilterCategory(e.target.value)}
      >
        <option value="">All Categories</option>
        {categories.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      {loading && <div style={{ padding: '12px 16px', color: '#888', fontSize: '13px' }}>Loading clauses...</div>}
      {!loading && filtered.length === 0 && (
        <div style={{ padding: '12px 16px', color: '#999', fontSize: '13px' }}>No clauses found. Add clauses via the Clause Library settings.</div>
      )}
      {filtered.map((clause) => (
        <div
          key={clause.id}
          className="doc-template-item"
          onClick={() => {
            if (editor) {
              editor.chain().focus().insertContent(clause.content).run();
              onInsert?.();
            }
          }}
        >
          <h4>{clause.title}</h4>
          <p>{clause.category}{clause.description ? ` — ${clause.description}` : ''}</p>
        </div>
      ))}
    </div>
  );
}

// ===================== VERSION HISTORY SIDEBAR =====================
function VersionHistorySidebar({ authAxios, documentId, editor, showFormMessage }) {
  const confirm = useConfirm();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!authAxios || !documentId) return;
    setLoading(true);
    authAxios.get(`documents/${documentId}/versions/`)
      .then((resp) => setVersions(resp.data?.results || resp.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authAxios, documentId]);

  const handleRestore = async (version) => {
    if (!(await confirm({ title: 'Restore Version', message: `Restore version ${version.version_number}? This will replace the current content.`, confirmText: 'Restore', variant: 'danger' }))) return;
    try {
      // Download version file and extract HTML
      const resp = await authAxios.get(`documents/${documentId}/versions/${version.id}/`, { responseType: 'json' });
      if (resp.data?.file) {
        // Fetch the HTML file content
        const fileResp = await authAxios.get(resp.data.file, { responseType: 'text' });
        if (editor && fileResp.data) {
          editor.commands.setContent(fileResp.data);
          showFormMessage?.(`Restored version ${version.version_number}.`, 'success');
        }
      }
    } catch (err) {
      console.error('Failed to restore version:', err);
      showFormMessage?.('Failed to restore version.', 'error');
    }
  };

  return (
    <div className="doc-template-sidebar">
      <h3>Version History</h3>
      {loading && <div style={{ padding: '12px 16px', color: '#888', fontSize: '13px' }}>Loading versions...</div>}
      {!loading && versions.length === 0 && (
        <div style={{ padding: '12px 16px', color: '#999', fontSize: '13px' }}>No versions yet. Versions are created automatically when you save.</div>
      )}
      {versions.map((v) => (
        <div key={v.id} className="doc-template-item" onClick={() => handleRestore(v)}>
          <h4>Version {v.version_number}</h4>
          <p>{new Date(v.created_at).toLocaleString()} — {v.change_description || 'Auto-saved'}</p>
        </div>
      ))}
    </div>
  );
}

// ===================== STATUS BADGE =====================
const STATUS_CONFIG = {
  draft: { label: 'Draft', color: '#757575', bg: '#f5f5f5' },
  pending_review: { label: 'Pending Review', color: '#e65100', bg: '#fff3e0' },
  approved: { label: 'Approved', color: '#2e7d32', bg: '#e8f5e9' },
  rejected: { label: 'Rejected', color: '#c62828', bg: '#ffebee' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <span className="doc-status-badge" style={{ color: cfg.color, backgroundColor: cfg.bg, border: `1px solid ${cfg.color}` }}>
      {cfg.label}
    </span>
  );
}

// ===================== MAIN EDITOR PAGE =====================
export default function DocumentEditorPage({
  authAxios,
  documentId,       // optional – if editing an existing ERP document
  initialContent,   // optional HTML string
  documentTitle,    // optional string
  onBack,           // callback to return to DocumentManagement
  showFormMessage,  // toast/message callback from App
  currentUser,      // { id, username, first_name, last_name, ... }
}) {
  const permissions = usePermissions() || {};
  const confirm = useConfirm();
  const [title, setTitle] = useState(documentTitle || 'Untitled Document');
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showClauses, setShowClauses] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [docStatus, setDocStatus] = useState('draft');
  const [lockedBy, setLockedBy] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const exportRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const [currentDocId, setCurrentDocId] = useState(documentId || null);

  const canApprove = permissions.isPartner || permissions.isSuperuser;
  const isReadOnly = docStatus === 'approved' || (lockedBy && lockedBy !== currentUser?.id);

  // TipTap Editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight.configure({ multicolor: true }),
      ImageExt.configure({ inline: true, allowBase64: true }),
      Link.extend({ name: 'custom-link' }).configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Placeholder.configure({ placeholder: 'Start typing your document...' }),
      CommentMark,
    ],
    content: initialContent || '',
    editable: !isReadOnly,
    onUpdate: ({ editor: ed }) => {
      setIsDirty(true);
      // Update word / char counts
      const text = ed.state.doc.textContent;
      setCharCount(text.length);
      setWordCount(text.trim() ? text.trim().split(/\s+/).length : 0);

      // Auto-save debounce (30 seconds)
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = setTimeout(() => {
        handleAutoSave();
      }, 30000);
    },
  });

  // Update editable state when status/lock changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!isReadOnly);
    }
  }, [isReadOnly, editor]);

  // Load existing document content & metadata
  useEffect(() => {
    if (currentDocId && authAxios && editor) {
      authAxios.get(`documents/${currentDocId}/`)
        .then((resp) => {
          const doc = resp.data;
          setTitle(doc.title || 'Untitled');
          setDocStatus(doc.status || 'draft');
          setLockedBy(doc.locked_by || null);
          setReviewNotes(doc.review_notes || '');
          if (doc.html_content) {
            editor.commands.setContent(doc.html_content);
          } else if (doc.description) {
            editor.commands.setContent(`<p>${doc.description}</p>`);
          }
          // Update counts
          const text = editor.state.doc.textContent;
          setCharCount(text.length);
          setWordCount(text.trim() ? text.trim().split(/\s+/).length : 0);
          setIsDirty(false);
        })
        .catch((err) => console.error('Failed to load document:', err));

      // Acquire lock
      authAxios.post(`documents/${currentDocId}/lock/`).catch(() => {});
    }

    // Release lock on unmount
    return () => {
      if (currentDocId && authAxios) {
        authAxios.post(`documents/${currentDocId}/unlock/`).catch(() => {});
      }
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [currentDocId, authAxios]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
      if (e.ctrlKey && e.key === 'p') {
        e.preventDefault();
        window.print();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [editor, currentDocId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close export dropdown
  useEffect(() => {
    const handler = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ---- Auto-save handler ----
  const handleAutoSave = useCallback(async () => {
    if (!editor || !authAxios || !currentDocId || isReadOnly) return;
    try {
      await authAxios.patch(`documents/${currentDocId}/`, {
        title,
        html_content: editor.getHTML(),
        auto_save: true,
      });
      setIsDirty(false);
      setLastSaved(new Date());
    } catch (err) {
      console.error('Auto-save failed:', err);
    }
  }, [editor, authAxios, currentDocId, isReadOnly, title]);

  // ---- Save to ERP backend ----
  const handleSave = useCallback(async () => {
    if (!editor || !authAxios) return;
    setSaving(true);
    try {
      const htmlContent = editor.getHTML();

      if (currentDocId) {
        await authAxios.patch(`documents/${currentDocId}/`, {
          title,
          html_content: htmlContent,
        });
      } else {
        const resp = await authAxios.post('documents/', {
          title,
          html_content: htmlContent,
          document_type: 'Other',
        });
        if (resp.data?.id) {
          setCurrentDocId(resp.data.id);
          showFormMessage?.(`Document "${title}" created successfully.`, 'success');
        }
      }
      setIsDirty(false);
      setLastSaved(new Date());
      showFormMessage?.('Document saved.', 'success');
    } catch (err) {
      console.error('Save failed:', err);
      showFormMessage?.('Failed to save document.', 'error');
    } finally {
      setSaving(false);
    }
  }, [editor, authAxios, currentDocId, title, showFormMessage]);

  // ---- Workflow actions ----
  const handleSubmitForReview = useCallback(async () => {
    if (!authAxios || !currentDocId) return;
    // Save first
    if (isDirty) await handleSave();
    try {
      await authAxios.post(`documents/${currentDocId}/submit-for-review/`);
      setDocStatus('pending_review');
      showFormMessage?.('Document submitted for review.', 'success');
    } catch (err) {
      showFormMessage?.('Failed to submit for review.', 'error');
    }
  }, [authAxios, currentDocId, isDirty, handleSave, showFormMessage]);

  const handleApprove = useCallback(async () => {
    if (!authAxios || !currentDocId) return;
    try {
      await authAxios.post(`documents/${currentDocId}/approve/`);
      setDocStatus('approved');
      showFormMessage?.('Document approved.', 'success');
    } catch (err) {
      showFormMessage?.('Failed to approve document.', 'error');
    }
  }, [authAxios, currentDocId, showFormMessage]);

  const handleReject = useCallback(async () => {
    if (!authAxios || !currentDocId || !rejectNotes.trim()) return;
    try {
      await authAxios.post(`documents/${currentDocId}/reject/`, { notes: rejectNotes });
      setDocStatus('rejected');
      setReviewNotes(rejectNotes);
      setShowRejectDialog(false);
      setRejectNotes('');
      showFormMessage?.('Document rejected.', 'success');
    } catch (err) {
      showFormMessage?.('Failed to reject document.', 'error');
    }
  }, [authAxios, currentDocId, rejectNotes, showFormMessage]);

  const handleRevertToDraft = useCallback(async () => {
    if (!authAxios || !currentDocId) return;
    try {
      await authAxios.post(`documents/${currentDocId}/revert-to-draft/`);
      setDocStatus('draft');
      showFormMessage?.('Document reverted to draft.', 'success');
    } catch (err) {
      showFormMessage?.('Failed to revert to draft.', 'error');
    }
  }, [authAxios, currentDocId, showFormMessage]);

  const handleSaveAsTemplate = useCallback(async () => {
    if (!editor || !authAxios || !currentDocId) return;
    const name = await confirm({ title: 'Save as Template', message: 'Enter a name for the template.', confirmText: 'Save', variant: 'primary', promptMode: true, promptLabel: 'Template name', required: true, promptDefault: title });
    if (!name) return;
    try {
      await authAxios.post(`documents/${currentDocId}/save-as-template/`, { name });
      showFormMessage?.(`Saved as template "${name}".`, 'success');
    } catch (err) {
      showFormMessage?.('Failed to save as template.', 'error');
    }
  }, [editor, authAxios, currentDocId, title, showFormMessage, confirm]);

  // ---- Export ----
  const handleExport = useCallback(async (format) => {
    if (!editor || !authAxios) return;
    try {
      const htmlContent = editor.getHTML();

      // Use the document export endpoint on the main API
      const exportUrl = currentDocId
        ? `documents/${currentDocId}/export/`
        : `documents/export/`;

      const resp = await authAxios.post(
        exportUrl,
        { content: htmlContent, format, title },
        { responseType: 'blob' }
      );

      const blob = new Blob([resp.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = format === 'docx' ? 'docx' : format === 'pdf' ? 'pdf' : 'html';
      a.download = `${title || 'document'}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showFormMessage?.(`Exported as ${format.toUpperCase()}.`, 'success');
    } catch (err) {
      console.error('Export failed:', err);
      showFormMessage?.(`Export failed: ${err.message}`, 'error');
    }
  }, [editor, authAxios, title, showFormMessage]);

  const handlePrint = useCallback(() => { window.print(); }, []);

  // Close sidebars helper
  const toggleSidebar = (name) => {
    setShowTemplates(name === 'templates' ? !showTemplates : false);
    setShowClauses(name === 'clauses' ? !showClauses : false);
    setShowComments(name === 'comments' ? !showComments : false);
    setShowVersions(name === 'versions' ? !showVersions : false);
  };

  return (
    <div className="doc-editor-page">
      {/* ===== Top Bar ===== */}
      <div className="doc-editor-topbar">
        <div className="doc-editor-topbar-left">
          {onBack && (
            <button onClick={async () => {
              if (isDirty && !(await confirm({ title: 'Unsaved Changes', message: 'You have unsaved changes. Leave anyway?', confirmText: 'Leave', variant: 'danger' }))) return;
              onBack();
            }} title="Back to Documents">
              <IconBack /> Back
            </button>
          )}
          <input
            className="doc-editor-title-input"
            value={title}
            onChange={(e) => { setTitle(e.target.value); setIsDirty(true); }}
            placeholder="Document title..."
          />
          <StatusBadge status={docStatus} />
        </div>
        <div className="doc-editor-topbar-right">
          {lastSaved && (
            <span className="doc-editor-save-indicator">
              Saved {lastSaved.toLocaleTimeString()}
            </span>
          )}
          {isDirty && (
            <span className="doc-editor-save-indicator" style={{ color: '#e65100' }}>
              Unsaved
            </span>
          )}
          {isReadOnly && (
            <span className="doc-editor-save-indicator" style={{ color: '#c62828' }}>
              Read-only
            </span>
          )}

          {/* Sidebar toggles */}
          <button onClick={() => toggleSidebar('comments')} title="Comments" className={showComments ? 'active' : ''}>
            <i className="bi bi-chat-dots" /> Comments
          </button>
          <button onClick={() => toggleSidebar('templates')} title="Legal Templates">
            Templates
          </button>
          <button onClick={() => toggleSidebar('clauses')} title="Clause Library">
            Clauses
          </button>
          {currentDocId && (
            <button onClick={() => toggleSidebar('versions')} title="Version History">
              Versions
            </button>
          )}

          <button onClick={handlePrint} title="Print (Ctrl+P)">
            <IconPrint /> Print
          </button>

          {/* Export Dropdown */}
          <div className="doc-export-dropdown" ref={exportRef}>
            <button onClick={() => setShowExportMenu(!showExportMenu)} title="Export">
              <IconExport /> Export
            </button>
            {showExportMenu && (
              <div className="doc-export-dropdown-menu">
                <button onClick={() => { handleExport('docx'); setShowExportMenu(false); }}>Export as Word (.docx)</button>
                <button onClick={() => { handleExport('pdf'); setShowExportMenu(false); }}>Export as PDF</button>
                <button onClick={() => { handleExport('html'); setShowExportMenu(false); }}>Export as HTML</button>
                {currentDocId && (
                  <button onClick={() => { handleSaveAsTemplate(); setShowExportMenu(false); }}>Save as Template</button>
                )}
              </div>
            )}
          </div>

          <button className="primary" onClick={handleSave} disabled={saving || isReadOnly}>
            <IconSave /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* ===== Approval Workflow Bar ===== */}
      {currentDocId && (
        <div className="doc-workflow-bar">
          {docStatus === 'draft' && (
            <button className="doc-workflow-btn submit" onClick={handleSubmitForReview}>
              Submit for Review
            </button>
          )}
          {docStatus === 'pending_review' && canApprove && (
            <>
              <button className="doc-workflow-btn approve" onClick={handleApprove}>
                Approve
              </button>
              <button className="doc-workflow-btn reject" onClick={() => setShowRejectDialog(true)}>
                Reject
              </button>
            </>
          )}
          {docStatus === 'pending_review' && !canApprove && (
            <span className="doc-workflow-info">Awaiting review by a partner or senior attorney.</span>
          )}
          {(docStatus === 'rejected' || docStatus === 'approved') && (
            <button className="doc-workflow-btn revert" onClick={handleRevertToDraft}>
              Revert to Draft
            </button>
          )}
          {docStatus === 'rejected' && reviewNotes && (
            <span className="doc-workflow-info doc-workflow-rejection">Rejection reason: {reviewNotes}</span>
          )}
          {docStatus === 'approved' && (
            <span className="doc-workflow-info doc-workflow-approved">This document is approved and read-only.</span>
          )}
        </div>
      )}

      {/* ===== Reject Dialog ===== */}
      {showRejectDialog && (
        <div className="doc-modal-overlay" onClick={() => setShowRejectDialog(false)}>
          <div className="doc-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Reject Document</h3>
            <p>Please provide a reason for rejection:</p>
            <textarea
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              rows={4}
              placeholder="Rejection reason..."
              style={{ width: '100%' }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button onClick={() => setShowRejectDialog(false)}>Cancel</button>
              <button className="primary" onClick={handleReject} disabled={!rejectNotes.trim()}>Confirm Rejection</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Formatting Toolbar ===== */}
      <EditorToolbar editor={editor} />

      {/* ===== Editor + Sidebars ===== */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <div className="doc-editor-body">
          <div className="doc-editor-paper">
            {docStatus === 'draft' && <div className="doc-draft-watermark">DRAFT</div>}
            <EditorContent editor={editor} />
          </div>
        </div>

        {/* Sidebars (only one visible at a time) */}
        {showTemplates && (
          <TemplateSidebar authAxios={authAxios} editor={editor} onInsert={() => setIsDirty(true)} />
        )}
        {showClauses && (
          <ClauseLibrarySidebar authAxios={authAxios} editor={editor} onInsert={() => setIsDirty(true)} />
        )}
        {showComments && (
          <DocumentCommentsSidebar
            authAxios={authAxios}
            documentId={currentDocId}
            editor={editor}
            currentUser={currentUser}
            showFormMessage={showFormMessage}
          />
        )}
        {showVersions && (
          <VersionHistorySidebar
            authAxios={authAxios}
            documentId={currentDocId}
            editor={editor}
            showFormMessage={showFormMessage}
          />
        )}
      </div>

      {/* ===== Status Bar ===== */}
      <div className="doc-status-bar">
        <span>{wordCount} words &middot; {charCount} characters</span>
        <span>{docStatus === 'draft' ? 'Editing' : STATUS_CONFIG[docStatus]?.label}</span>
        {lastSaved && <span>Last saved {lastSaved.toLocaleTimeString()}</span>}
        {lockedBy && lockedBy !== currentUser?.id && <span style={{ color: '#c62828' }}>Locked by another user</span>}
      </div>
    </div>
  );
}
